import { useMemo } from "react";
import { reservationResultSelector, reservationTypesSelector } from "../selectors/Selectors";
import { TReservationTypeLid } from "../store/types";
import useAppSelector from "./useAppSelector";

const formatTitle = (title: string) => {
    if (!title) return "";

    // Mapping of special characters to their replacements
    const charMap = new Map([
        ["å", "a"],
        ["ä", "a"],
        ["ö", "o"],
        ["æ", "ae"],
        ["ø", "o"],
        [" ", "-"],
        [",", "-"],
        [".", "-"],
    ]);

    // Convert to lowercase and trim spaces.
    title = title.trim().toLowerCase();

    // Replace special characters first.
    title = title.replace(/[åäöæø ,.]/g, match => charMap.get(match) || match);

    // Remove non-alphanumeric characters (except hyphen), normalize hyphens, and trim.
    title = title
        .replace(/[^a-z0-9-]/g, "") // Remove non-alphanumeric characters (except hyphen).
        .replace(/-+/g, "-") // Normalize multiple hyphens to a single one.
        .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens.

    // Return URL encoded title.
    return encodeURIComponent(title);
};

const getPathSegment = (id: number, title?: string) => {
    if (!title) return `/${id}`;

    return `/${formatTitle(title)}-${id}`;
};

export const usePathBuilder = () => {
    const reservationTypes = useAppSelector(reservationTypesSelector);
    const reservationResult = useAppSelector(reservationResultSelector);

    const buildPath = useMemo(
        () => (reservationTypeId: TReservationTypeLid, groupPoollid?: number, poollid?: number, unitlid?: number) => {
            const reservationType = reservationTypes.find(resvType => resvType.id === reservationTypeId);

            let url = groupPoollid ? "/product" : "/search";

            // Add reservation type.
            url += getPathSegment(reservationTypeId, reservationType?.description);

            // Add group.
            if (groupPoollid) {
                const groupPool = reservationResult[reservationTypeId]?.groups?.find(
                    group => group.grouppoollid === groupPoollid
                );

                url += getPathSegment(groupPoollid, groupPool?.groupitem?.title);

                const pool = groupPool?.items?.find(pool => pool.poollid === poollid && pool.unitlid === unitlid);

                if (pool) {
                    const isPool = pool.poollid && !pool.unitlid;
                    const isUnit = pool.poollid && pool.unitlid;

                    // If pool, add pool title and lid to url.
                    if (isPool) {
                        url += `${getPathSegment(pool.poollid, pool.title)}/0`;
                    }

                    // If unit, add only pool lid, followed by unit title and lid.
                    if (isUnit) {
                        url += `/${pool.poollid}${getPathSegment(pool.unitlid, pool.title)}`;
                    }
                }
            }

            return url;
        },
        [reservationTypes, reservationResult]
    );

    return { buildPath };
};
